// "Attach transcript" — the field that turns a meeting into a reading (BUILD-BRIEF-R2 §7).
//
// It lives as its own component rather than inside the Log-a-touchpoint modal because that
// modal belongs to `components/Timeline.tsx` (the interactions agent, round 1). Dropping this
// one line into the modal's body is the whole integration:
//
//   {kind === 'meeting' && (
//     <TranscriptUpload
//       occurredOn={occurredOn} title={title}
//       personIds={participants.map((p) => p.person_id).filter((id): id is number => id != null)}
//       organisationId={org?.id ?? null}
//       onUploaded={(r) => { onLogged?.(r.interaction); onClose(); }} />
//   )}
//
// WHAT IT DOES, IN ONE SENTENCE: uploading IS the save. `POST /api/transcripts` writes the
// meeting interaction with the transcript as its body — the operator's own material, imported
// by the operator's own click — and then starts a `transcript` run that PROPOSES the
// commitments, facts and people it reads out of the prose. That is why the panel closes the
// modal on success: the touchpoint is logged, and clicking Save afterwards would log a second.
//
// The honest states it renders and never fakes: a file too large or of the wrong type is
// refused before anything is sent; a stored transcript whose run could not start says so in
// words and keeps the interaction; a run that started names its ref and says its findings will
// arrive as proposals, marked ◇, because nobody has held them.
import { useRef, useState } from 'react';
import { uploadTranscript, type TranscriptUploadResult } from '../api/transcripts.js';
import { ApiError } from '../api/client.js';
import { Att, Fig, Ref } from './PageKit.js';
import { useT } from '../i18n/index.js';
import './TranscriptUpload.de.js';
import './TranscriptUpload.fr.js';

/** The same three the route accepts (services/triage.ts) — stated here so the picker filters
 *  and the refusal below reads the same list the server would answer with. */
const ACCEPT = '.txt,.vtt,.md';
const MAX_BYTES = 5 * 1024 * 1024;

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot <= 0 ? '' : name.slice(dot).toLowerCase();
};

export interface TranscriptUploadProps {
  /** The day the meeting was held, YYYY-MM-DD. Required by the route. */
  occurredOn: string;
  title?: string;
  personIds?: number[];
  organisationId?: number | null;
  disabled?: boolean;
  onUploaded?: (result: TranscriptUploadResult) => void;
}

export default function TranscriptUpload({
  occurredOn, title, personIds, organisationId, disabled, onUploaded,
}: TranscriptUploadProps) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<TranscriptUploadResult | null>(null);

  const choose = (chosen: File | null): void => {
    setDone(null);
    if (!chosen) { setFile(null); setError(null); return; }
    if (!['.txt', '.vtt', '.md'].includes(extensionOf(chosen.name))) {
      setFile(null);
      setError(t('A transcript is a .txt, .vtt or .md file. That one is not.'));
      return;
    }
    if (chosen.size > MAX_BYTES) {
      setFile(null);
      setError(t('That file is larger than 5 MB. Nothing was sent.'));
      return;
    }
    setError(null);
    setFile(chosen);
  };

  const send = async (): Promise<void> => {
    if (!file || busy) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(occurredOn)) {
      setError(t('Give the date the meeting was held before attaching its transcript.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await uploadTranscript(file, {
        occurred_on: occurredOn,
        title: title?.trim() || undefined,
        person_ids: personIds ?? [],
        organisation_id: organisationId ?? null,
      });
      setDone(result);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      onUploaded?.(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('The transcript could not be uploaded.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form-group">
      <label className="form-label" htmlFor="transcript-file">{t('Attach transcript')}</label>
      <input
        id="transcript-file"
        ref={inputRef}
        className="form-input"
        type="file"
        accept={ACCEPT}
        disabled={disabled || busy}
        onChange={(e) => choose(e.target.files?.[0] ?? null)}
      />

      {file && (
        <p className="zone-empty">
          <Ref>{file.name}</Ref>{' '}
          <Fig unit={t('kB')}>{Math.max(1, Math.round(file.size / 1024))}</Fig>
        </p>
      )}

      {error && <p className="zone-empty fv--broken">{error}</p>}

      {!done && (
        <>
          <button type="button" className="btn" disabled={!file || busy || disabled} onClick={() => { void send(); }}>
            {busy ? t('Uploading…') : t('Upload transcript')}
          </button>
          <p className="zone-empty">
            {t('Uploading logs the meeting with the transcript as its body — that is the save. A reading then proposes the commitments and the facts it finds; nothing reaches the record without your click.')}
          </p>
        </>
      )}

      {done && (
        <p className="zone-empty">
          {t('Logged as')} <Ref>{done.interaction.ref}</Ref>
          {done.run
            ? (
              <>
                {' · '}<Att kind="proposed" />
                {t('reading it as run')} <Ref>{done.run.ref}</Ref>{' — '}
                {t('what it finds arrives in Review as proposals.')}
              </>
            )
            : (
              <>
                {' · '}
                <span className="fv--attend">
                  {t('the transcript is on the record, but no reading started:')} {done.run_error ?? t('the model endpoint did not answer.')}
                </span>
              </>
            )}
        </p>
      )}
    </div>
  );
}
